import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Box, Stack, Divider, Typography, Paper, IconButton, Tooltip, Drawer, Button, Tabs, Tab, Switch, FormControlLabel } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SettingsIcon from "@mui/icons-material/Settings";

import { HttpAdminApi } from "../../interface/admin-api";
import { HttpChannelApi } from "../../interface/channel-api";
import IoTClient from "../../utils/iot/IoTClient";
import { useInterval } from "../../utils/UseInterval";
import { useWindowSize } from "../../utils/useWindowSize";
import { CommonUtils } from "../../utils/common_utils";

import { userState, iotConfig } from "../../interface/MainInterface";
import LoadingCircle from "../../utils/LoadingCircle";
import Toast from "../../utils/Toast";

import ChannelManageModule from "./ChannelManage/ChannelManageModule";
import LiveStatistic from "./Statistic/LiveStatistic";
import LivePreviewModule from "./Preview/LivePreviewModule";
import LiveChatModule from "./Chat/LiveChatModule";
import NoticeManagementOld from "./ChannelManage/NoticeManagementOld";
import VodManagement from "./Vod/VodManagement";
import LiveEventManagement from "./Event/LiveEventManagement";
import ChangeBroadTitle from "./ChannelManage/ChangeBroadTitle";
import LiveCoupon from "./Coupon/LiveCoupon";
import FAQManagement from "./ChannelManage/FAQManagement";
import { ReplayManagement } from "./Replay/ReplayManagement";
import BannerAdManagement from "./ChannelManage/BannerAdManagement";

interface propsType {
  userState: userState;
}

const adminApi = new HttpAdminApi();
const channelApi = new HttpChannelApi();
const cUtils = new CommonUtils();

// .content-content-root 의 아래쪽 여백(12px)에 약간의 여유를 더한 값이다.
const MAIN_CONTENT_BOTTOM_GAP = 16;
const MAIN_CONTENT_MIN_HEIGHT = 320;

const LiveDetail = (props: propsType) => {
  let navigate = useNavigate();
  const windowSize = useWindowSize();
  const toastRef: any = useRef();
  const [isLoading, setIsLoading] = useState(false);

  const iotRef: any = useRef();
  const liveChatPcRef: any = useRef();
  const liveEventRef: any = useRef();
  const livePreviewRef: any = useRef();
  const changeBroadTitleRef: any = useRef();

  const { broadSeq } = useParams();
  const [selectedReplayVideo, setSelectedReplayVideo] = useState<any>(null);

  const [channelInfo, setChannelInfo] = useState<any>({});
  const [poolVod, setPoolVod] = useState(false);

  const [mainContentHeight, setMainContentHeight] = useState(620);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [openChannelDetail, setOpenChannelDetail] = useState(false);

  const [pageHeigth, setPageHeight] = useState(0);

  const [tabName, setTabName] = useState("coupon");

  useEffect(() => {
    get_channel_info();
    return () => {
      setIsLoading(false);
      setChannelInfo({});
      setPoolVod(false);
      setMainContentHeight(620);
      setOpenChannelDetail(false);
      setPageHeight(0);
    };
  }, []);

  const get_channel_info = async () => {
    const param: any = {
      command: "get_channel_info",
      broad_seq: broadSeq,
    };

    const res = await adminApi.post(param);
    if (res.code === "200") {
      const info: any = res.response.channel_info;
      info.channel_pk = `broad_date#${dayjs(info.broad_start_tm).format("YYYYMMDD")}`;
      setChannelInfo(res.response.channel_info);
      console.log("channelInfo : ", res.response.channel_info);
      if (res.response.channel_info.broad_status === "VOD") {
        setPoolVod(false);
      }

      get_replay_info();
    } else {
      console.error("getChannelInfo 에러 발생 : " + res.response.error_msg);
    }
  };

  const get_replay_info = async () => {
    try {
      const param = {
        command: "get_replay_info",
        broad_seq: broadSeq
      };

      const res = await adminApi.post(param);
      if (res.code === "200" && res.response.replay_channel_info) {
        if (res.response.replay_channel_info.length > 0) {
          setSelectedReplayVideo(res.response.replay_channel_info[0]);
        }
      } else {
        setSelectedReplayVideo(null); // 설정이 없거나 use_yn이 N인 경우
      }
    } catch (e) {
      console.error("재방송 설정 조회에러 : ", e);
    }
  };

  const cancel_replay_info = async () => {
    // 1. 클릭 시점에 실시간으로 방송 시작 시간 1시간 전 여부 체크
    const startTime = dayjs(channelInfo.broad_start_tm, "YYYYMMDDHHmmss");
    const isDisableCancel = dayjs().add(1, 'hour').isAfter(startTime);

    console.log("cancel_replay_info startTime : ", startTime);
    console.log("cancel_replay_info isDisableCancel : ", isDisableCancel);

    if (isDisableCancel) {
      toastRef.current?.toast("방송 시작 1시간 전부터는 재방송 설정을 변경할 수 없습니다.", "error", 3000, {
        vertical: "top",
        horizontal: "center",
      });

      // 2. 새로고침을 통해 버튼 비활성화 상태로 변경
      await get_channel_info();
      return; // 함수 조기 종료
    }

    // 사용자 실수 방지를 위한 컨펌창
    if (!window.confirm("설정된 재방송 정보를 해제하시겠습니까?")) return;

    try {
      const param = {
        command: "update_replay_info", // 백엔드 명령어
        search_condition: {
          channel_info: channelInfo, // 현재 라이브 채널의 번호
          use_yn: "N"
        },
        // request_user_id: props.userState.id,
      };

      const res = await adminApi.post(param);

      if (res.code === "200") {
        // 1. 상태값 초기화 (UI에서 정보 바 사라짐)
        setSelectedReplayVideo(null);

        toastRef.current?.toast("재방송 설정이 해제되었습니다.", "success", 3000, {
          vertical: "top",
          horizontal: "center",
        });

        // 3. 필요 시 채널 정보 재조회
        get_channel_info();
      } else {
        toastRef.current?.toast("설정 해제에 실패했습니다.", "error", 3000, {
          vertical: "top",
          horizontal: "center",
        });
      }
    } catch (error) {
      console.error("재방송 설정 해제 에러:", error);
      toastRef.current?.toast("서버 통신 중 에러가 발생했습니다.", "error", 3000, {
        vertical: "top",
        horizontal: "center",
      });
    }
  };

  const update_replay_benefit_notice = async (showBenefitNotice: boolean) => {
    try {
      const param = {
        command: "update_replay_benefit_notice",
        search_condition: {
          channel_info: channelInfo,
          show_benefit_notice: showBenefitNotice,
        },
      };

      const res = await adminApi.post(param);

      if (res.code === "200") {
        setSelectedReplayVideo((prev: any) => (
          prev ? { ...prev, show_benefit_notice: showBenefitNotice } : prev
        ));
        sendIoTMessage({
          topic: "live/metadata",
          message: "updateReplayBenefitNotice",
          showBenefitNotice,
        });

        toastRef.current?.toast(
          showBenefitNotice ? "재방송 혜택 안내 문구가 노출됩니다." : "재방송 혜택 안내 문구가 숨겨집니다.",
          "success",
          3000,
          {
            vertical: "top",
            horizontal: "center",
          }
        );
      } else {
        toastRef.current?.toast("재방송 혜택 안내 문구 설정 변경에 실패했습니다.", "error", 3000, {
          vertical: "top",
          horizontal: "center",
        });
      }
    } catch (error) {
      console.error("재방송 혜택 안내 문구 설정 변경 에러:", error);
      toastRef.current?.toast("서버 통신 중 에러가 발생했습니다.", "error", 3000, {
        vertical: "top",
        horizontal: "center",
      });
    }
  };

  // IoT 연결 성공
  const iotConnectEnd = () => {
    console.info("iot Connection Success");
  };

  const iotLost = () => {
    console.info("iot Lost >>> Reload");
    channelInfo?.broad_seq !== undefined && iotRef.current?.connect(); // IoT다시 맺기
  };

  // IoT message recieve callback
  function receiveIoTMobileMessage(topic: any, payLoad: any, clientId: string) {
    if (topic === `live/${channelInfo.broad_seq}/payload`) {
      if (payLoad.topic === "chat/message") {
        // 일반 메세지 도착
        liveChatPcRef.current?.processChannelMessage(payLoad);
      } else if (payLoad.topic === "event/update" && payLoad.prot === "END") {
        // 이벤트 리스트 조회
        const event_seq = payLoad.event_seq;
        liveEventRef.current.get_event_list_total(event_seq);
      }
    }
  }

  const sendIoTMessageMeta = (metaName: string) => {
    const topic = `live/${channelInfo?.broad_seq}/payload`;
    iotRef.current?.sendIoTMessageMeta(topic, metaName);
  };

  const sendIoTMessage = (payLoad: any) => {
    const topic = `live/${channelInfo?.broad_seq}/payload`;
    iotRef.current?.sendIoTMessage(topic, payLoad);
  };

  const openChangeBroadTitle = () => {
    changeBroadTitleRef.current.open();
  };

  const deleteChannel = async () => {
    // 권한
    if (!props.userState.isSuperAdmin && props.userState.id !== channelInfo.host_id) {
      alert("다른 사람의 채널을 수정하실 수 없습니다.");
      return;
    }
    if (channelInfo.broad_status === "START") {
      alert("현재 방송중인 채널입니다. 먼저 방송을 종료 후 다시 시도하세요.");
      return;
    }
    if (!window.confirm("채널을 삭제 하겠습니까?\n한번 삭제한 채널은 복구할 수 없습니다.")) return;

    const param = {
      command: "delete_channel",
      broad_info: {
        pk: channelInfo.channel_pk,
        broad_seq: channelInfo.broad_seq,
      },
      request_user_id: props.userState.id,
    };
    setIsLoading(true);
    const res = await channelApi.post(param);
    if (res.result_code === "200") {
      navigate("/list");
    } else {
      alert(`[ERROR] ${res.result_body.error_msg}`);
    }
    setIsLoading(false);
  };

  const delay: number = 5000; // 5초마다 변경
  useInterval(
    () => {
      get_channel_info();
    },
    poolVod ? delay : null
  );

  // 하단 영역(미리보기·채팅·쿠폰·버튼)의 높이를 화면에 남은 공간으로 채운다.
  // 라이브 보드처럼 위쪽 요소가 빠지면 시작 위치가 올라가므로, 상수로 빼지 않고 실제 위치를 측정한다.
  useLayoutEffect(() => {
    const resizeMainContent = () => {
      if (windowSize.width <= 768) {
        setMainContentHeight(windowSize.height - 310);
        return;
      }

      const mainContentNode = mainContentRef.current;
      if (mainContentNode === null) return;

      const restHeight = window.innerHeight - mainContentNode.getBoundingClientRect().top - MAIN_CONTENT_BOTTOM_GAP;
      setMainContentHeight(Math.max(Math.round(restHeight), MAIN_CONTENT_MIN_HEIGHT));
    };

    resizeMainContent();
    // 글꼴이나 상단 요소가 늦게 자리를 잡는 경우가 있어서 한 번 더 측정한다.
    const timer = window.setTimeout(resizeMainContent, 0);
    return () => window.clearTimeout(timer);
  }, [
    windowSize.width,
    windowSize.height,
    channelInfo,
    selectedReplayVideo,
    props.userState.isAdmin,
    props.userState.isSuperAdmin,
  ]);

  useEffect(() => {
    setPageHeight(windowSize.height);
  }, [windowSize.height]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabName(newValue);
  };

  return (
    <>
      <Box sx={{ p: 0, height: "100%" }}>
        <Stack direction={"column"} spacing={0}>
          <Box className={windowSize.width > 768 ? "content-header-root" : "content-header-root-mo"}>
            <Stack direction={"row"} spacing={windowSize.width > 768 ? 2 : 1} sx={{ alignItems: "center" }}>
              {windowSize.width > 768 ? (
                <Typography variant="h5" className={"content-header-title"}>
                  {`방송 진행 관리 : ${channelInfo?.broad_title
                    ? `${channelInfo?.broad_title} - [${dayjs(channelInfo.broad_start_tm).format("YY-MM-DD")}]`
                    : ""
                    }`}
                </Typography>
              ) : (
                <Typography variant="h5" className={"content-header-title-mo"}>
                  {`${channelInfo?.broad_title
                    ? `${channelInfo?.broad_title} - [${dayjs(channelInfo.broad_start_tm).format("YY-MM-DD")}]`
                    : ""
                    }`}
                </Typography>
              )}

              {props.userState.id === channelInfo.host_id && (
                <Tooltip title="방송명 번경">
                  <Box>
                    <IconButton onClick={openChangeBroadTitle}>
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              )}
            </Stack>
            {windowSize.width > 768 && (
              <Stack direction={"row"} spacing={2} sx={{ alignItems: "center" }}>
                {props.userState.isSuperAdmin && (
                  <Tooltip title="방송삭제">
                    <Box>
                      <IconButton onClick={deleteChannel}>
                        <DeleteForeverIcon color="error" />
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
          <Divider />

          {/* --- [추가] 재방송 설정 정보 바 --- */}
          {selectedReplayVideo && (() => {
            // 1. 재방송 시작 시간을 dayjs 객체로 변환
            const startTime = dayjs(channelInfo.broad_start_tm, "YYYYMMDDHHmmss");

            // 2. 비활성화 조건: 시작 시간 1시간 전 (현재 시간 + 1시간 > 시작 시간 이면 비활성화)
            const isDisableCancel = dayjs().add(1, 'hour').isAfter(startTime);

            return (
              <Box
                sx={{
                  backgroundColor: "#edf7ed",
                  borderBottom: "1px solid #c3e6cb",
                  px: 3, py: 1,
                  display: "flex", alignItems: "center", gap: 2
                }}
              >
                <Box sx={{
                  backgroundColor: "#4caf50", color: "#fff", px: 1.5, py: 0.5,
                  borderRadius: "4px", fontSize: "12px", fontWeight: "bold"
                }}>
                  재방송 모드
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e4620" }}>
                    {selectedReplayVideo.vod_broad_title}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", color: "#4caf50" }}>
                    (원본 방송일: {dayjs(selectedReplayVideo.vod_broad_start_tm, "YYYYMMDDHHmmss").format("YYYY-MM-DD")})
                  </Typography>
                </Stack>

                <Tooltip title="재방송 혜택 안내 문구 노출 여부">
                  <span>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={selectedReplayVideo.show_benefit_notice !== false}
                          onChange={(event) => update_replay_benefit_notice(event.target.checked)}
                        />
                      }
                      label="재방송 문구 노출"
                      sx={{
                        mr: 0,
                        "& .MuiFormControlLabel-label": {
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#1e4620",
                        },
                      }}
                    />
                  </span>
                </Tooltip>

                <Tooltip title={isDisableCancel ? "방송 시작 1시간 전부터는 설정을 해제할 수 없습니다." : "재방송 설정 해제"}>
                  <span>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={isDisableCancel} // 조건에 따른 비활성화
                      onClick={cancel_replay_info}
                      sx={{ fontSize: "12px", fontWeight: "bold" }}
                    >
                      설정 해제
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            );
          })()}
          {/* ---------------------------------- */}

          <Box className="content-content-root">
            <Stack direction={"column"} spacing={2}>
              {windowSize.width > 768 ? (
                <>
                  {/* 방송 관리 영역 */}
                  <ChannelManageModule
                    userState={props.userState}
                    channelInfo={channelInfo}
                    callback={(payload: any) => {
                      if (payload.command === "loading") {
                        setIsLoading(payload.value);
                      } else if (payload.command === "toast") {
                        toastRef.current?.toast(payload.msg, payload.type, payload.sec, {
                          vertical: "top",
                          horizontal: "center",
                        });
                      } else if (payload.command === "update_stream") {
                        // IOT 전송
                        sendIoTMessageMeta(payload.value);
                        get_channel_info();
                        // make vod
                        if (payload.value === "stop_stream") {
                          setPoolVod(true);
                          if (payload.result.is_ncp_success === false) {
                            toastRef.current?.toast("[허브] 이벤트 데이터 전송 실패, 담당자 문의 바랍니다.", "error", 5000, {
                              vertical: "top",
                              horizontal: "center",
                            });
                          }
                        }
                      } else if (payload.command === "change_password") {
                        get_channel_info();
                      }
                    }}
                  />
                  {/* 라이브 통계 영역 */}
                  <LiveStatistic
                    userState={props.userState}
                    channelInfo={channelInfo}
                    callback={(payload: any) => {
                      if (payload.command === "extend_broad_time") {
                        get_channel_info();
                      }
                    }}
                  />
                </>
              ) : (
                /* 라이브 통계 영역 */
                <LiveStatistic
                  userState={props.userState}
                  channelInfo={channelInfo}
                  callback={(payload: any) => {
                    if (payload.command === "extend_broad_time") {
                      get_channel_info();
                    }
                  }}
                />
              )}

              <Box ref={mainContentRef}>
                <Stack direction={windowSize.width > 768 ? "row" : "column"} spacing={2}>
                  {windowSize.width > 768 && (
                    /* 방송 미리보기 */
                    <Paper
                      sx={{
                        width: `${(mainContentHeight * 9) / 16}px`,
                        minWidth: `${(mainContentHeight * 9) / 16}px`,
                        height: `${mainContentHeight}px`,
                      }}
                      elevation={5}
                    >
                      <LivePreviewModule
                        ref={livePreviewRef}
                        userState={props.userState}
                        channelInfo={channelInfo}
                        callback={() => { }}
                      />
                    </Paper>
                  )}

                  {/* 채팅 화면 */}
                  <Paper sx={{ width: `100%` }} elevation={5}>
                    {props.userState.id !== "" && !cUtils.isEmptyObj(channelInfo) && (
                      <Box
                        sx={{
                          position: "relative",
                          height: `${mainContentHeight}px`,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <LiveChatModule
                          ref={liveChatPcRef}
                          channelInfo={channelInfo}
                          userState={props.userState}
                          sendIoTMessage={sendIoTMessage}
                          callback={(payload: any) => {
                            if (payload.command === "put_pin_message") {
                              // Iot 전송
                              console.log("payload: ", payload);
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Paper>
                  <Box sx={{ width: "100%", maxWidth: "400px", height: `${mainContentHeight}px` }}>
                    <Paper sx={{ height: "100%" }} elevation={5}>
                      <Tabs
                        value={tabName}
                        onChange={handleTabChange}
                        TabIndicatorProps={{
                          style: { backgroundColor: "#000" },
                        }}
                        sx={{
                          height: "48px",
                          ".Mui-selected": {
                            color: "#000000 !important",
                          },
                        }}
                      >
                        <Tab value="coupon" label="쿠폰" sx={{ fontFamily: "Noto Sans KR" }} />
                      </Tabs>
                      {tabName === "coupon" && <LiveCoupon userState={props.userState} channelInfo={channelInfo} />}
                    </Paper>
                  </Box>
                  {/* 버튼 영역 */}
                  <Box sx={{ minWidth: "100px", height: windowSize.width > 768 ? `${mainContentHeight}px` : "auto" }}>
                    {/* 노트북처럼 세로가 짧은 화면에서는 버튼이 영역을 넘치므로 스크롤로 보여준다. */}
                    <Paper sx={{ height: "100%", overflowY: "auto", scrollbarWidth: "thin" }} elevation={5}>
                      <Stack direction={windowSize.width > 768 ? "column" : "row"} spacing={1} sx={{ p: 1 }}>
                        {windowSize.width < 768 && (
                          <Box
                            sx={{ cursor: "pointer", minWidth: "70px" }}
                            onClick={() => {
                              setOpenChannelDetail(true);
                            }}
                          >
                            <Stack
                              direction={"column"}
                              spacing={0}
                              sx={{ backgroundColor: "#1876d2", color: "#ffffff", borderRadius: "7px", p: 1 }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                                <SettingsIcon />
                              </Box>
                              <Typography sx={{ fontSize: "14px", textAlign: "center" }}>방송관리</Typography>
                            </Stack>
                          </Box>
                        )}
                        {/* 공지사항관리 */}
                        <NoticeManagementOld
                          userState={props.userState}
                          channelInfo={channelInfo}
                          heigth={windowSize.height}
                          callback={(payload: any) => {
                            if (payload.command === "update_broad_notice") {
                              toastRef.current?.toast("공지사항을 수정했습니다.", "success", 3000, {
                                vertical: "top",
                                horizontal: "center",
                              });
                              sendIoTMessageMeta("reloadChannelInfo");
                              get_channel_info();
                            }
                          }}
                        />
                        {/* FAQ 관리 */}
                        <FAQManagement
                          userState={props.userState}
                          broad_seq={broadSeq!}
                          callback={(payload: any) => { }}
                        />

                        {/* 배너 광고 관리 */}
                        <BannerAdManagement
                          userState={props.userState}
                          channelInfo={channelInfo}
                          callback={(payload: any) => {
                            if (payload.command === "update_banner_ad") {
                              toastRef.current?.toast("배너 광고 설정을 저장했습니다.", "success", 3000, {
                                vertical: "top",
                                horizontal: "center",
                              });
                              sendIoTMessageMeta("reloadChannelInfo");
                              get_channel_info();
                            }
                          }}
                        />

                        {/* VOD관리 */}
                        {channelInfo.broad_status === "VOD" &&
                          props.userState.isAdmin && (
                            <VodManagement
                              userState={props.userState}
                              channelInfo={channelInfo}
                              heigth={pageHeigth}
                              callback={(payload: any) => {
                                if (payload.command === "change_vod") {
                                  get_channel_info();
                                  livePreviewRef.current.reload();
                                }
                              }}
                            />
                          )}
                        {/* 이벤트관리 */}
                        <LiveEventManagement
                          ref={liveEventRef}
                          userState={props.userState}
                          channelInfo={channelInfo}
                          callback={(payload: any) => { }}
                        />
                        {/* --- 추가된 재방송 설정 기능 --- */}
                        <ReplayManagement
                          userState={props.userState}
                          channelInfo={channelInfo}
                          callback={(payload: any) => {
                            if (payload.command === "set_replay_video") {
                              // 상태 업데이트 (화면 표시용)
                              setSelectedReplayVideo(payload.value);

                              toastRef.current?.toast("재방송 영상이 설정되었습니다.", "success", 3000, {
                                vertical: "top",
                                horizontal: "center",
                              });
                              get_channel_info(); // 채널 정보 새로고침
                              if (livePreviewRef.current) livePreviewRef.current.reload(); // 미리보기 갱신
                            } else if (payload.command === "refresh_channel") {
                              get_channel_info();
                            }
                          }}
                        />
                        {/* ---------------------------- */}
                      </Stack>
                    </Paper>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
      <LoadingCircle loading={isLoading} />
      <Toast ref={toastRef} />
      <ChangeBroadTitle
        ref={changeBroadTitleRef}
        userState={props.userState}
        channelInfo={channelInfo}
        callback={(payload: any) => {
          if (payload.command === "finish_change_title") {
            toastRef.current?.toast("방송명을 변경했습니다.", "success", 4000, {
              vertical: "top",
              horizontal: "center",
            });
            get_channel_info();
          }
        }}
      />
      {channelInfo?.broad_seq !== undefined && (
        <IoTClient
          ref={iotRef}
          broadSeq={channelInfo?.broad_seq}
          iotRegion={iotConfig.region}
          iotIdentityPoolId={iotConfig.identityPollId}
          iotEndPoint={iotConfig.endpoint}
          recieveIoTMessage={receiveIoTMobileMessage}
          iotConnectEnd={iotConnectEnd}
          iotLost={iotLost}
        />
      )}
      {/* 모바일 버전 방송관리 */}
      <Drawer
        anchor={windowSize.width > 768 ? "right" : "top"}
        open={openChannelDetail}
        onClose={() => {
          setOpenChannelDetail(false);
        }}
      >
        <Box sx={{ width: windowSize.width > 768 ? "500px" : "100vw" }}>
          <Box sx={{ p: 2 }}>
            <Stack direction={"column"} spacing={2}>
              {/* 방송 관리 영역 */}
              <ChannelManageModule
                userState={props.userState}
                channelInfo={channelInfo}
                callback={(payload: any) => {
                  if (payload.command === "loading") {
                    setIsLoading(payload.value);
                  } else if (payload.command === "toast") {
                    toastRef.current?.toast(payload.msg, payload.type, payload.sec, {
                      vertical: "top",
                      horizontal: "center",
                    });
                  } else if (payload.command === "update_stream") {
                    // IOT 전송
                    sendIoTMessageMeta(payload.value);
                    get_channel_info();
                    // make vod
                    if (payload.value === "stop_stream") {
                      setPoolVod(true);
                      if (payload.result.is_ncp_success === false) {
                        toastRef.current?.toast("[허브] 이벤트 데이터 전송 실패, 담당자 문의 바랍니다.", "error", 5000, {
                          vertical: "top",
                          horizontal: "center",
                        });
                      }
                    }
                  } else if (payload.command === "change_password") {
                    get_channel_info();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenChannelDetail(false);
                }}
              >
                닫기
              </Button>
            </Stack>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default LiveDetail;
